import DOMPurify from 'dompurify';
import getFilterableContent from './js/fetch';
import { setParams, getParams } from './js/params.js';
import setInputs from './js/inputs';
import { clearFilters } from './js/active-filters.js';
import publishFilterBarHeight from './js/filter-bar-height';
import { slideUp, slideDown } from '../../js/theme/helpers';

/**
 * Initiate filterable content blocks logic.
 */
const initFilterableContent = () => {
	// Get all filterable content blocks.
	const filterableContentBlock = document.querySelectorAll(
		'.wp-block-embark-filterable-content',
	);

	// Loop through each block and apply logic only to that block.
	filterableContentBlock.forEach((block) => {
		const contentContainer = block.querySelector(
			'.wp-block-embark-filterable-content__listings',
		);
		const filterContainer = block.querySelector(
			'.wp-block-embark-filterable-content__filter-container',
		);
		const inputs = block.querySelectorAll('input, select');
		const searchButton = block.querySelector('.search');
		const filterControl = block.querySelector(
			'.wp-block-embark-filterable-content__filter-control',
		);
		const filterInner = block.querySelector(
			'.wp-block-embark-filterable-content__filter-inner',
		);

		// Let the chat launcher and anything else pinned to the viewport bottom
		// track the sticky filter bar, which grows as the drawer slides open.
		publishFilterBarHeight(filterContainer);

		// Process filters and search values.
		const getFilteredContent = () => {
			filterContainer.dataset.paged = 1;
			setParams(block);
			const params = getParams(block);
			getFilterableContent(contentContainer, params);
		};

		// Loop through inputs and set event handlers.
		inputs.forEach((input) => {
			// The "All …" row at the top of a dropdown is a reset, not a filter of
			// its own — it reports through emListboxChange below, so its checkbox
			// must not also fetch on its way past.
			if (input.closest('.listbox__option--all')) {
				return;
			}

			if (input.type === 'checkbox' || input.tagName === 'SELECT') {
				input.addEventListener('change', () => {
					getFilteredContent();
				});
			} else {
				input.addEventListener('keyup', (e) => {
					if (e.key === 'Enter') {
						getFilteredContent();
					}
				});
			}
		});

		// Picking "All …" at the top of a dropdown clears that dropdown only, and
		// leaves every other filter standing — "All Solutions" means all solutions,
		// not all resources. Clear Results is the control that drops everything.
		//
		// The listbox has already unticked its own checkboxes by the time this
		// fires, and says so once rather than once per option, so there is nothing
		// left to clear here: refetching picks up whatever the other dropdowns and
		// the search box still have set. fetch.js re-runs setActiveFilters after
		// every fetch, which rebuilds the pills and hides the footer if that was
		// the last filter standing.
		block.addEventListener('emListboxChange', (e) => {
			if (e.detail && e.detail.cleared) {
				getFilteredContent();
			}
		});

		// If search input not empty submit search.
		searchButton.addEventListener('click', (e) => {
			const button = e.target.closest('button');
			if (button.previousElementSibling.value) {
				getFilteredContent();
			}
		});

		if (filterControl && filterInner) {
			const updateFilterAccordion = () => {
				if (embark.breakpoints.above('sm')) {
					filterInner.style = null;
					filterControl.innerHTML = DOMPurify.sanitize(
						filterControl.dataset.defaultText,
					);
					filterControl.setAttribute('aria-expanded', 'false');
				} else {
					filterControl.innerHTML = DOMPurify.sanitize(
						filterControl.dataset.openText,
					);
					// Maintain current aria-expanded state on mobile
					if (!filterControl.hasAttribute('aria-expanded')) {
						filterControl.setAttribute('aria-expanded', 'false');
					}
				}
			};

			const openFilterAccordion = () => {
				filterControl.classList.add('open');
				filterControl.innerHTML = DOMPurify.sanitize(
					filterControl.dataset.closeText,
				);
				filterControl.setAttribute('aria-expanded', 'true');
				slideDown(filterInner, '.3s', 'flex');
			};

			const closeFilterAccordion = () => {
				filterControl.classList.remove('open');
				filterControl.innerHTML = DOMPurify.sanitize(
					filterControl.dataset.openText,
				);
				filterControl.setAttribute('aria-expanded', 'false');
				slideUp(filterInner);
			};

			filterControl.addEventListener('click', () => {
				if (embark.breakpoints.below('sm')) {
					if (filterControl.classList.contains('open')) {
						closeFilterAccordion();
					} else {
						openFilterAccordion();
					}
				} else {
					updateFilterAccordion();
				}
			});

			document.addEventListener('click', (e) => {
				if (embark.breakpoints.below('sm')) {
					const { target } = e;
					const closestFilterControl = target.closest(
						'.wp-block-embark-filterable-content__filter-control',
					);
					const closestFilterInner = target.closest(
						'.wp-block-embark-filterable-content__filter-inner',
					);
					if (!closestFilterControl && !closestFilterInner) {
						closeFilterAccordion();
					}
				}
			});

			window.addEventListener('resize', () => {
				window.requestAnimationFrame(() => {
					updateFilterAccordion();
				});
			});

			updateFilterAccordion();
		}

		// Set exisitng values from query string.
		setInputs(block);

		// Get parameters.
		const params = getParams(block);

		// Get initial list of posts and output.
		getFilterableContent(contentContainer, params);

		// Initiate clear filters button..
		clearFilters(block, inputs, getFilterableContent);

		window.addEventListener(
			'popstate',
			() => {
				// Set exisitng values from query string.
				setInputs(block);
				getFilterableContent(contentContainer, getParams(block));
			},
			false,
		);
	});
};

document.addEventListener('DOMContentLoaded', initFilterableContent);
